import { cva, type VariantProps } from "class-variance-authority"
import * as React from "react"

import { cn } from "@/utils/styles.helpers"

/**
 * Tags: quiet by default. Tone variants (ok, warn, bad, info, neutral) are a
 * soft background with coloured text; prefer `StatusDot` from `./status` for
 * normal states and keep tinted tags for states that need someone.
 */
const badgeVariants = cva(
	"inline-flex h-5 items-center gap-1 whitespace-nowrap rounded-[4px] border border-transparent px-1.5 text-[11.5px] leading-none font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring [&_svg]:size-3 [&_svg]:shrink-0",
	{
		variants: {
			variant: {
				default: "bg-primary/14 text-primary-text",
				secondary: "bg-foreground/8 text-muted-foreground",
				destructive: "bg-bad-soft text-bad",
				outline: "border-border text-muted-foreground",
				alert: "bg-bad-soft text-bad",
				idle: "text-muted-foreground",
				ok: "bg-ok-soft text-ok",
				warn: "bg-warn-soft text-warn",
				bad: "bg-bad-soft text-bad",
				info: "bg-info-soft text-info",
				neutral: "bg-foreground/8 text-muted-foreground",
				// Game-specific variants: quiet tints
				mtg: "bg-orange-500/12 text-orange-700 dark:bg-orange-400/12 dark:text-orange-300",
				pokemon:
					"bg-blue-500/12 text-blue-700 dark:bg-blue-400/12 dark:text-blue-300",
				lorcana:
					"bg-violet-500/12 text-violet-700 dark:bg-violet-400/12 dark:text-violet-300",
				fab: "bg-rose-500/12 text-rose-700 dark:bg-rose-400/12 dark:text-rose-300",
				sorcery:
					"bg-emerald-500/12 text-emerald-700 dark:bg-emerald-400/12 dark:text-emerald-300",
				wankul:
					"bg-sky-500/12 text-sky-700 dark:bg-sky-400/12 dark:text-sky-300",
				onepiece:
					"bg-blue-500/12 text-blue-700 dark:bg-blue-400/12 dark:text-blue-300",
				ygo: "bg-fuchsia-500/12 text-fuchsia-700 dark:bg-fuchsia-400/12 dark:text-fuchsia-300",
				"ygo-ocg":
					"bg-purple-500/12 text-purple-700 dark:bg-purple-400/12 dark:text-purple-300",
				riftbound:
					"bg-cyan-500/12 text-cyan-700 dark:bg-cyan-400/12 dark:text-cyan-300",
				rise: "bg-green-500/12 text-green-700 dark:bg-green-400/12 dark:text-green-300",

				// Finish-specific variants: quiet tints
				"finish-standard":
					"bg-slate-500/12 text-slate-700 dark:bg-slate-400/12 dark:text-slate-300",
				"finish-foil":
					"bg-amber-500/12 text-amber-700 dark:bg-amber-400/12 dark:text-amber-300",
				"finish-etched":
					"bg-violet-500/12 text-violet-700 dark:bg-violet-400/12 dark:text-violet-300",
				"finish-signed":
					"bg-stone-500/12 text-stone-700 dark:bg-stone-400/12 dark:text-stone-300",
				"finish-holo":
					"bg-sky-500/12 text-sky-700 dark:bg-sky-400/12 dark:text-sky-300",
				"finish-reverse-holo":
					"bg-blue-500/12 text-blue-700 dark:bg-blue-400/12 dark:text-blue-300",
				"finish-rainbow":
					"bg-gradient-to-r from-rose-500/15 via-amber-500/15 to-emerald-500/15 text-foreground",
				"finish-cold":
					"bg-cyan-500/12 text-cyan-700 dark:bg-cyan-400/12 dark:text-cyan-300",
				"finish-gold":
					"bg-yellow-500/12 text-yellow-700 dark:bg-yellow-400/12 dark:text-yellow-300",
				"finish-galaxy":
					"bg-gradient-to-r from-indigo-500/15 via-purple-500/15 to-pink-500/15 text-foreground",
				"finish-glitter":
					"bg-pink-500/12 text-pink-700 dark:bg-pink-400/12 dark:text-pink-300",
				"finish-lenticular":
					"bg-indigo-500/12 text-indigo-700 dark:bg-indigo-400/12 dark:text-indigo-300",
				"finish-surge":
					"bg-teal-500/12 text-teal-700 dark:bg-teal-400/12 dark:text-teal-300",
				"finish-star":
					"bg-amber-500/12 text-amber-700 dark:bg-amber-400/12 dark:text-amber-300",
				"finish-special":
					"bg-fuchsia-500/12 text-fuchsia-700 dark:bg-fuchsia-400/12 dark:text-fuchsia-300",
			},
		},
		defaultVariants: {
			variant: "default",
		},
	},
)

export interface BadgeProps
	extends React.HTMLAttributes<HTMLDivElement>,
		VariantProps<typeof badgeVariants> {}

/** A small tag; pick a tone variant for status and the game or finish variants for catalogue labels. */
function Badge({ className, variant, ...props }: BadgeProps) {
	return (
		<div className={cn(badgeVariants({ variant }), className)} {...props} />
	)
}

export { Badge, badgeVariants }
